import { zodResolver } from "@hookform/resolvers/zod";
import type { SchedulingLink } from "@shared/schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Briefcase,
  Calendar,
  Check,
  Loader2,
  Moon,
  Pencil,
  Plus,
  Save,
  Star,
  Sun,
  Trash2,
  User,
  X,
} from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { PageHeader } from "@/components/page-header";
import { useTheme } from "@/components/theme-provider";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { getDepartmentLabel, getJobLevelLabel } from "@/lib/constants";
import { queryClient } from "@/lib/queryClient";
import { orpc, rpcClient } from "@/lib/rpc";

const profileFormSchema = z.object({
  fullName: z.string().min(2, "Nome deve ter pelo menos 2 caracteres"),
  personalEmail: z.string().email("Email inválido").optional().or(z.literal("")),
  phone: z.string().optional().or(z.literal("")),
  cpf: z.string().optional().or(z.literal("")),
  birthdate: z.string().optional().or(z.literal("")),
  address: z.string().optional().or(z.literal("")),
  emergencyContact: z.string().optional().or(z.literal("")),
});

type ProfileFormValues = z.infer<typeof profileFormSchema>;

const DURATION_OPTIONS = [
  { value: 15, label: "15 min" },
  { value: 30, label: "30 min" },
  { value: 45, label: "45 min" },
  { value: 60, label: "60 min" },
  { value: 90, label: "90 min" },
  { value: 120, label: "120 min" },
];

function SchedulingLinksSection() {
  const { toast } = useToast();
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newUrl, setNewUrl] = useState("");
  const [newDuration, setNewDuration] = useState<string>("");
  const [editUrl, setEditUrl] = useState("");
  const [editDuration, setEditDuration] = useState<string>("");

  const { data: links = [], isLoading } = useQuery(orpc.profile.getSchedulingLinks.queryOptions());

  const createMutation = useMutation({
    mutationFn: async (data: {
      url: string;
      durationMinutes: number | null;
      isDefault: boolean;
    }) => {
      return rpcClient.profile.createSchedulingLink(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.profile.getSchedulingLinks.queryOptions().queryKey,
      });
      queryClient.invalidateQueries({ queryKey: orpc.profile.getProfile.queryOptions().queryKey });
      queryClient.invalidateQueries({ queryKey: orpc.dashboard.people.queryOptions().queryKey });
      setIsAdding(false);
      setNewUrl("");
      setNewDuration("");
      toast({ title: "Link adicionado" });
    },
    onError: (error: any) => {
      toast({
        title: "Erro",
        description: error.message || "Falha ao adicionar link",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: { url?: string; durationMinutes?: number | null; isDefault?: boolean };
    }) => {
      return rpcClient.profile.updateSchedulingLink({ id, ...data });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.profile.getSchedulingLinks.queryOptions().queryKey,
      });
      queryClient.invalidateQueries({ queryKey: orpc.profile.getProfile.queryOptions().queryKey });
      queryClient.invalidateQueries({ queryKey: orpc.dashboard.people.queryOptions().queryKey });
      setEditingId(null);
      toast({ title: "Link atualizado" });
    },
    onError: (error: any) => {
      toast({
        title: "Erro",
        description: error.message || "Falha ao atualizar link",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await rpcClient.profile.deleteSchedulingLink({ id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.profile.getSchedulingLinks.queryOptions().queryKey,
      });
      queryClient.invalidateQueries({ queryKey: orpc.profile.getProfile.queryOptions().queryKey });
      queryClient.invalidateQueries({ queryKey: orpc.dashboard.people.queryOptions().queryKey });
      toast({ title: "Link removido" });
    },
    onError: (error: any) => {
      toast({
        title: "Erro",
        description: error.message || "Falha ao remover link",
        variant: "destructive",
      });
    },
  });

  const handleAdd = () => {
    if (!newUrl.trim()) return;
    createMutation.mutate({
      url: newUrl.trim(),
      durationMinutes: newDuration ? parseInt(newDuration, 10) : null,
      isDefault: links.length === 0,
    });
  };

  const handleSaveEdit = (link: SchedulingLink) => {
    if (!editUrl.trim()) return;
    updateMutation.mutate({
      id: link.id,
      data: {
        url: editUrl.trim(),
        durationMinutes: editDuration ? parseInt(editDuration, 10) : null,
      },
    });
  };

  const handleSetDefault = (id: string) => {
    updateMutation.mutate({ id, data: { isDefault: true } });
  };

  const startEditing = (link: SchedulingLink) => {
    setEditingId(link.id);
    setEditUrl(link.url);
    setEditDuration(link.durationMinutes?.toString() || "");
  };

  const getDurationLabel = (minutes: number | null) => {
    if (!minutes) return null;
    const opt = DURATION_OPTIONS.find((o) => o.value === minutes);
    return opt?.label || `${minutes} min`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {links.length === 0 && !isAdding && (
        <p className="text-sm text-muted-foreground">Nenhum link de agendamento configurado.</p>
      )}

      {links.map((link) => (
        <div
          key={link.id}
          className="flex items-center gap-2 p-3 rounded-md border bg-card"
          data-testid={`scheduling-link-${link.id}`}
        >
          {editingId === link.id ? (
            <div className="flex-1 space-y-2">
              <Input
                value={editUrl}
                onChange={(e) => setEditUrl(e.target.value)}
                placeholder="https://cal.com/..."
                data-testid="input-edit-scheduling-url"
              />
              <div className="flex items-center gap-2">
                <select
                  value={editDuration}
                  onChange={(e) => setEditDuration(e.target.value)}
                  className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                  data-testid="select-edit-scheduling-duration"
                >
                  <option value="">Qualquer duração</option>
                  {DURATION_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleSaveEdit(link)}
                  disabled={updateMutation.isPending}
                  data-testid="button-confirm-edit-link"
                >
                  <Check className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditingId(null)}
                  data-testid="button-cancel-edit-link"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium truncate">{link.url}</p>
                  {link.isDefault && (
                    <Badge variant="secondary" className="shrink-0 text-xs">
                      <Star className="h-3 w-3 mr-1" />
                      Principal
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {getDurationLabel(link.durationMinutes) || "Qualquer duração"}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {!link.isDefault && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleSetDefault(link.id)}
                    title="Tornar principal"
                    data-testid={`button-set-default-${link.id}`}
                  >
                    <Star className="h-3.5 w-3.5" />
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => startEditing(link)}
                  data-testid={`button-edit-link-${link.id}`}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  onClick={() => deleteMutation.mutate(link.id)}
                  disabled={deleteMutation.isPending}
                  data-testid={`button-delete-link-${link.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </>
          )}
        </div>
      ))}

      {isAdding ? (
        <div className="space-y-2 p-3 rounded-md border border-dashed">
          <Input
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            placeholder="https://cal.com/..."
            autoFocus
            data-testid="input-new-scheduling-url"
          />
          <div className="flex items-center gap-2">
            <select
              value={newDuration}
              onChange={(e) => setNewDuration(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              data-testid="select-new-scheduling-duration"
            >
              <option value="">Qualquer duração</option>
              {DURATION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <div className="flex-1" />
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setIsAdding(false);
                setNewUrl("");
                setNewDuration("");
              }}
              data-testid="button-cancel-add-link"
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              onClick={handleAdd}
              disabled={!newUrl.trim() || createMutation.isPending}
              data-testid="button-confirm-add-link"
            >
              {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsAdding(true)}
          data-testid="button-add-scheduling-link"
        >
          <Plus className="h-4 w-4 mr-1" />
          Adicionar link
        </Button>
      )}
    </div>
  );
}

export default function ProfilePage() {
  const { toast } = useToast();
  const { theme, setTheme } = useTheme();

  const { data: employee, isLoading } = useQuery(orpc.profile.getProfile.queryOptions());

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      fullName: "",
      personalEmail: "",
      phone: "",
      cpf: "",
      birthdate: "",
      address: "",
      emergencyContact: "",
    },
    values: employee
      ? {
          fullName: employee.fullName || "",
          personalEmail: employee.personalEmail || "",
          phone: employee.phone || "",
          cpf: employee.cpf || "",
          birthdate: employee.birthdate || "",
          address: employee.address || "",
          emergencyContact: (employee.emergencyContact as string) || "",
        }
      : undefined,
  });

  const updateMutation = useMutation({
    mutationFn: async (data: ProfileFormValues) => {
      return rpcClient.profile.updateProfile(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.profile.getProfile.queryOptions().queryKey });
      queryClient.invalidateQueries({ queryKey: orpc.auth.me.queryOptions().queryKey });
      toast({
        title: "Perfil atualizado",
        description: "Suas informações foram salvas com sucesso.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao atualizar",
        description: error.message || "Não foi possível salvar as alterações.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: ProfileFormValues) => {
    updateMutation.mutate(data);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-muted-foreground" data-testid="text-no-profile">
          Perfil não encontrado.
        </p>
      </div>
    );
  }

  const companyInfo = [
    { label: "Email corporativo", value: employee.corporateEmail, testId: "text-corporate-email" },
    { label: "Cargo", value: employee.jobTitle, testId: "text-job-title" },
    {
      label: "Nível",
      value: employee.jobLevel ? getJobLevelLabel(employee.jobLevel) : null,
      testId: "text-job-level",
    },
    {
      label: "Departamento",
      value: employee.department ? getDepartmentLabel(employee.department) : null,
      testId: "text-department",
    },
    { label: "Tipo de contrato", value: employee.employmentType, testId: "text-employment-type" },
    { label: "Tipo de trabalho", value: employee.workType, testId: "text-work-type" },
    { label: "Data de início", value: employee.startDate, testId: "text-start-date" },
  ];

  return (
    <div className="space-y-6 max-w-4xl">
      <PageHeader title="Meu Perfil" description="Visualize e edite suas informações pessoais" />

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <Briefcase className="h-4 w-4" />
            Informações da Empresa
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-4">
            {companyInfo.map((item) => (
              <div key={item.testId}>
                <p className="text-xs text-muted-foreground mb-0.5">{item.label}</p>
                <p className="text-sm font-medium" data-testid={item.testId}>
                  {item.value || "\u2014"}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <Calendar className="h-4 w-4" />
            Links de Agendamento
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Configure seus links de agendamento (Calendly, Cal.com, etc.) para entrevistas. Você
            pode ter links diferentes para durações diferentes.
          </p>
        </CardHeader>
        <CardContent>
          <SchedulingLinksSection />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <Sun className="h-4 w-4" />
            Aparência
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {theme === "dark" ? (
                <Moon className="h-5 w-5 text-muted-foreground" />
              ) : (
                <Sun className="h-5 w-5 text-muted-foreground" />
              )}
              <div>
                <Label className="text-sm font-medium">Modo escuro</Label>
                <p className="text-xs text-muted-foreground">Alterne entre tema claro e escuro</p>
              </div>
            </div>
            <Switch
              checked={theme === "dark"}
              onCheckedChange={(checked) => setTheme(checked ? "dark" : "light")}
              data-testid="switch-dark-mode"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <User className="h-4 w-4" />
            Informações Pessoais
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                <FormField
                  control={form.control}
                  name="fullName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome completo</FormLabel>
                      <FormControl>
                        <Input {...field} data-testid="input-fullName" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="personalEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email pessoal</FormLabel>
                      <FormControl>
                        <Input {...field} type="email" data-testid="input-personalEmail" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Telefone</FormLabel>
                      <FormControl>
                        <Input {...field} data-testid="input-phone" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="cpf"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CPF</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="XXX.XXX.XXX-XX" data-testid="input-cpf" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="birthdate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Data de nascimento</FormLabel>
                      <FormControl>
                        <Input {...field} type="date" data-testid="input-birthdate" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Endereço</FormLabel>
                    <FormControl>
                      <Input {...field} data-testid="input-address" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="emergencyContact"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contato de emergência</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Nome - Telefone"
                        data-testid="input-emergencyContact"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex justify-end pt-2">
                <Button
                  type="submit"
                  disabled={updateMutation.isPending}
                  data-testid="button-save-profile"
                >
                  {updateMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Salvar alterações
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
